import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Modal from 'react-modal';
import { programsApi } from '../../../api/adminApi';
import './ProgramsList.css';

const DEFAULT_LIMIT = 10;

function ProgramsList() {
  const location = useLocation();
  const navigate = useNavigate();
  const [programs, setPrograms] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(DEFAULT_LIMIT);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formAgeMin, setFormAgeMin] = useState('');
  const [formAgeMax, setFormAgeMax] = useState('');
  const [formFeatureTimeline, setFormFeatureTimeline] = useState('');
  const [formTeacherRatio, setFormTeacherRatio] = useState('');
  const [formIcon, setFormIcon] = useState(null);
  const [formPhoto, setFormPhoto] = useState(null);
  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadPrograms = (pageNum = page) => {
    setLoading(true);
    setError('');
    programsApi
      .list(pageNum, limit)
      .then((res) => {
        setPrograms(res.list);
        setTotal(res.total);
        setPage(res.page);
      })
      .catch((err) => setError(err.userMessage || 'Failed to load programs'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadPrograms(1);
  }, []);

  useEffect(() => {
    const editProgram = location.state?.editProgram;
    if (editProgram) {
      navigate(location.pathname, { replace: true, state: {} });
      openEditModal(editProgram);
    }
  }, [location.state?.editProgram]);

  const handleDeleteClick = (program) => setDeleteId(program);

  const handleDeleteConfirm = () => {
    if (!deleteId) return;
    setDeleting(true);
    programsApi
      .delete(deleteId.id)
      .then(() => {
        setDeleteId(null);
        loadPrograms(page);
      })
      .catch((err) => setError(err.userMessage || 'Failed to delete'))
      .finally(() => setDeleting(false));
  };

  const handleDeleteCancel = () => setDeleteId(null);

  const openAddModal = () => {
    setEditingProgram(null);
    setFormName('');
    setFormDescription('');
    setFormAgeMin('');
    setFormAgeMax('');
    setFormFeatureTimeline('');
    setFormTeacherRatio('');
    setFormIcon(null);
    setFormPhoto(null);
    setFormError('');
    setFormModalOpen(true);
  };

  const openEditModal = (program) => {
    setEditingProgram(program);
    setFormName(program?.name ?? program?.title ?? '');
    setFormDescription(program?.description ?? program?.desc ?? '');
    setFormAgeMin(program?.age_min != null ? String(program.age_min) : '');
    setFormAgeMax(program?.age_max != null ? String(program.age_max) : '');
    setFormFeatureTimeline(program?.feature_timeline ?? '');
    setFormTeacherRatio(program?.teacher_ratio ?? program?.feature_ratio ?? '');
    setFormIcon(null);
    setFormPhoto(null);
    setFormError('');
    setFormModalOpen(true);
  };

  const closeFormModal = () => {
    setFormModalOpen(false);
    setEditingProgram(null);
  };

  const [photoPreviewUrl, setPhotoPreviewUrl] = useState(null);
  const [iconPreviewUrl, setIconPreviewUrl] = useState(null);

  useEffect(() => {
    if (formPhoto instanceof File) {
      const url = URL.createObjectURL(formPhoto);
      setPhotoPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setPhotoPreviewUrl(editingProgram?.photo_url ?? editingProgram?.photoUrl ?? null);
  }, [formPhoto, editingProgram?.photo_url, editingProgram?.photoUrl]);

  useEffect(() => {
    if (formIcon instanceof File) {
      const url = URL.createObjectURL(formIcon);
      setIconPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setIconPreviewUrl(editingProgram?.icon_url ?? editingProgram?.iconUrl ?? null);
  }, [formIcon, editingProgram?.icon_url, editingProgram?.iconUrl]);

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setFormError('');
    if (!formName.trim()) {
      setFormError('Name is required.');
      return;
    }
    if (!formDescription.trim()) {
      setFormError('Description is required.');
      return;
    }
    const ageMinNum = formAgeMin === '' ? NaN : Number(formAgeMin);
    if (Number.isNaN(ageMinNum) || ageMinNum < 0) {
      setFormError('Age min must be a number (0 or greater).');
      return;
    }
    if (!formFeatureTimeline.trim()) {
      setFormError('Feature timeline is required.');
      return;
    }
    if (!formTeacherRatio.trim()) {
      setFormError('Teacher ratio is required.');
      return;
    }
    setFormSaving(true);
    const fields = {
      name: formName.trim(),
      description: formDescription.trim(),
      age_min: ageMinNum,
      age_max: formAgeMax === '' ? undefined : Number(formAgeMax),
      feature_timeline: formFeatureTimeline.trim(),
      feature_ratio: formTeacherRatio.trim(),
      icon: formIcon instanceof File ? formIcon : null,
      photo: formPhoto instanceof File ? formPhoto : null,
    };
    const promise = editingProgram
      ? programsApi.update(editingProgram.id, fields)
      : programsApi.create(fields);
    promise
      .then(() => {
        closeFormModal();
        loadPrograms(page);
      })
      .catch((err) => {
        setFormError(err.userMessage || 'Something went wrong.');
      })
      .finally(() => setFormSaving(false));
  };

  const programName = (p) => p.name || p.title || p.programName || '—';
  const programDesc = (p) => p.description || p.desc || '';
  const programAge = (p) => {
    const min = p.age_min ?? p.ageMin;
    const max = p.age_max ?? p.ageMax;
    if (min != null && max != null && max > 0) return `${min}–${max}`;
    if (min != null) return `${min}+`;
    return p.ageRange || p.age_range || p.age || '—';
  };
  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="admin-programs">
      <div className="admin-programs-header">
        <h2>Programs</h2>
        <button type="button" className="admin-programs-add" onClick={openAddModal}>
          Add program
        </button>
      </div>

      {error && (
        <div className="admin-programs-error" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <div className="admin-programs-loading">Loading programs…</div>
      ) : (
        <div className="admin-programs-table-wrap">
          <table className="admin-programs-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Age</th>
                <th>Description</th>
                <th className="admin-programs-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {programs.length === 0 ? (
                <tr>
                  <td colSpan={4} className="admin-programs-empty">
                    No programs yet. <button type="button" className="admin-programs-empty-btn" onClick={openAddModal}>Add one</button>.
                  </td>
                </tr>
              ) : (
                programs.map((p) => (
                  <tr key={p.id}>
                    <td>{programName(p)}</td>
                    <td>{programAge(p)}</td>
                    <td className="admin-programs-desc">{(programDesc(p) || '').slice(0, 80)}{(programDesc(p) || '').length > 80 ? '…' : ''}</td>
                    <td className="admin-programs-actions">
                      <Link to={`/admin/programs/${p.id}`} className="admin-programs-btn admin-programs-btn-view">
                        View
                      </Link>
                      <button
                        type="button"
                        className="admin-programs-btn admin-programs-btn-edit"
                        onClick={() => openEditModal(p)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="admin-programs-btn admin-programs-btn-delete"
                        onClick={() => handleDeleteClick(p)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {!loading && total > 0 && totalPages > 1 && (
        <div className="admin-programs-pagination">
          <button
            type="button"
            className="admin-programs-page-btn"
            onClick={() => loadPrograms(page - 1)}
            disabled={page <= 1}
          >
            Previous
          </button>
          <span className="admin-programs-page-info">
            Page {page} of {totalPages} ({total} total)
          </span>
          <button
            type="button"
            className="admin-programs-page-btn"
            onClick={() => loadPrograms(page + 1)}
            disabled={page >= totalPages}
          >
            Next
          </button>
        </div>
      )}

      <Modal
        isOpen={formModalOpen}
        onRequestClose={closeFormModal}
        contentLabel={editingProgram ? 'Edit program' : 'Add program'}
        className="admin-programs-add-modal"
        overlayClassName="admin-programs-add-modal-overlay"
      >
        <div className="admin-programs-add-modal-inner">
          <h3>{editingProgram ? 'Edit program' : 'Add program'}</h3>
          <form onSubmit={handleFormSubmit}>
            {formError && (
              <div className="admin-program-form-error" role="alert">
                {formError}
              </div>
            )}
            <div className="admin-program-form-field">
              <label htmlFor="form-program-name">Name *</label>
              <input
                id="form-program-name"
                type="text"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="e.g. Preschool"
                disabled={formSaving}
              />
            </div>
            <div className="admin-program-form-field">
              <label htmlFor="form-program-desc">Description *</label>
              <textarea
                id="form-program-desc"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder="Short description of the program"
                rows={2}
                disabled={formSaving}
              />
            </div>
            <div className="admin-program-form-row">
              <div className="admin-program-form-field">
                <label htmlFor="form-program-age-min">Age min *</label>
                <input
                  id="form-program-age-min"
                  type="number"
                  min={0}
                  value={formAgeMin}
                  onChange={(e) => setFormAgeMin(e.target.value)}
                  placeholder="0"
                  disabled={formSaving}
                />
              </div>
              <div className="admin-program-form-field">
                <label htmlFor="form-program-age-max">Age max</label>
                <input
                  id="form-program-age-max"
                  type="number"
                  min={0}
                  value={formAgeMax}
                  onChange={(e) => setFormAgeMax(e.target.value)}
                  placeholder="Optional"
                  disabled={formSaving}
                />
              </div>
            </div>
            <div className="admin-program-form-field">
              <label htmlFor="form-program-feature-timeline">Feature timeline *</label>
              <input
                id="form-program-feature-timeline"
                type="text"
                value={formFeatureTimeline}
                onChange={(e) => setFormFeatureTimeline(e.target.value)}
                placeholder="e.g. 9am–3pm"
                disabled={formSaving}
              />
            </div>
            <div className="admin-program-form-field">
              <label htmlFor="form-program-teacher-ratio">Teacher ratio *</label>
              <input
                id="form-program-teacher-ratio"
                type="text"
                value={formTeacherRatio}
                onChange={(e) => setFormTeacherRatio(e.target.value)}
                placeholder="e.g. 1:10"
                disabled={formSaving}
              />
            </div>
            <div className="admin-program-form-field">
              <label htmlFor="form-program-icon">Icon (image)</label>
              {iconPreviewUrl && (
                <div className="admin-program-form-image-preview">
                  <img src={iconPreviewUrl} alt="Current icon" />
                </div>
              )}
              <input
                id="form-program-icon"
                type="file"
                accept="image/*"
                onChange={(e) => setFormIcon(e.target.files?.[0] || null)}
                disabled={formSaving}
              />
            </div>
            <div className="admin-program-form-field">
              <label htmlFor="form-program-photo">Photo (image)</label>
              {photoPreviewUrl && (
                <div className="admin-program-form-image-preview admin-program-form-image-preview--large">
                  <img src={photoPreviewUrl} alt="Current photo" />
                </div>
              )}
              <input
                id="form-program-photo"
                type="file"
                accept="image/*"
                onChange={(e) => setFormPhoto(e.target.files?.[0] || null)}
                disabled={formSaving}
              />
            </div>
            <div className="admin-program-form-actions">
              <button type="button" className="admin-program-form-cancel" onClick={closeFormModal} disabled={formSaving}>
                Cancel
              </button>
              <button type="submit" className="admin-program-form-submit" disabled={formSaving}>
                {formSaving ? (editingProgram ? 'Updating…' : 'Creating…') : (editingProgram ? 'Update' : 'Create')}
              </button>
            </div>
          </form>
        </div>
      </Modal>

      {deleteId && (
        <div className="admin-programs-modal" role="dialog" aria-modal="true" aria-labelledby="delete-title">
          <div className="admin-programs-modal-backdrop" onClick={handleDeleteCancel} aria-hidden="true" />
          <div className="admin-programs-modal-box">
            <h3 id="delete-title">Delete program?</h3>
            <p>This will permanently delete “{programName(deleteId)}”. This cannot be undone.</p>
            <div className="admin-programs-modal-actions">
              <button
                type="button"
                className="admin-programs-btn admin-programs-btn-edit"
                onClick={handleDeleteCancel}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="admin-programs-btn admin-programs-btn-delete"
                onClick={handleDeleteConfirm}
                disabled={deleting}
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProgramsList;
